import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Eye, EyeOff, Mail, ChevronLeft } from 'lucide-react';
import { validatePassword } from '../../utils/validation';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/auth';
import { LoadingSpinner } from '../LoadingSpinner';
import ConfirmationModal from '../auth/ConfirmationModal';
import { EnrollMFA } from '../auth/EnrollMFA';
import { Button } from '../common/Button';

interface EditProfileSecurityProps {
  onSave?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
  isOnboarding?: boolean;
}

const EditProfileSecurity: React.FC<EditProfileSecurityProps> = ({
  onSave,
  onNext,
  onPrevious,
  onSkip,
  isOnboarding = false,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [oldEmail, setOldEmail] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const { updatePassword, loading, updateEmail, resendConfirmationEmail, doesEmailExist, user } =
    useAuth();

  const [isNewEmailConfirmationOpen, setIsNewEmailConfirmationOpen] = useState(false);
  const [isEmailChangedOpen, setIsEmailChangedOpen] = useState(false);
  const [isCurrentEmailConfirmationOpen, setIsCurrentEmailConfirmationOpen] = useState(false);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const isPasswordFormValid = currentPassword && newPassword;
  const isEmailFormValid = oldEmail && newEmail;

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !user?.email) {
      toast.error(t('errors.passwordInvalid'));
      return;
    }

    const errors = validatePassword(newPassword);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    } else {
      setPasswordErrors([]);
    }

    try {
      const result = await updatePassword(currentPassword, newPassword, user?.email);

      if (result === 'Invalid login credentials') {
        setPasswordError(t('currentPasswordDoesNotMatch', { ns: 'auth' }));
        return;
      }
      toast.success(t('passwordUpdated', { ns: 'auth' }));
      setCurrentPassword('');
      setNewPassword('');
      setShowCurrent(false);
      setShowNew(false);
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(t('resetPassword.updateError', { ns: 'auth' }));
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    if (!newEmail || !oldEmail) {
      toast.error(t('fieldRequired'));
      return;
    }

    if (oldEmail !== user?.email) {
      setEmailError(t('currentEmailDoesNotMatch', { ns: 'auth' }));
      return;
    }

    const emailExist = await doesEmailExist(newEmail);
    if (emailExist === true) {
      setEmailError(t('emailTakenException', { ns: 'auth' }));
      return;
    }

    try {
      await updateEmail(newEmail);
      setIsCurrentEmailConfirmationOpen(true);
    } catch (error) {
      console.error('Error updating email:', error);
      setEmailError(t('updateEmailFailed'));
      setIsCurrentEmailConfirmationOpen(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const text = {
    title: t('changeEmail', { ns: 'auth' }),
    heading: t('confirmEmail.sendLinkCurrentEmail', { ns: 'auth' }),
    securityNotice: t('confirmEmail.securityNotice', { ns: 'auth' }),
    confirmationNotice: t('confirmEmail.confirmationNotice', { ns: 'auth' }),
    emailInstruction: t('confirmEmail.emailInstruction', { ns: 'auth' }),
    emailHighlight: oldEmail,
    closeButton: t('confirmEmail.close', { ns: 'auth' }),
    closeButtonHint: t('confirmEmail.confirmReceived', { ns: 'auth' }),
    resendButton: t('confirmEmail.resend', { ns: 'auth' }),
    editButton: t('changeEmail', { ns: 'auth' }),
  };

  const text2 = {
    title: t('changeEmail', { ns: 'auth' }),
    heading: t('confirmEmail.sendLinkNewEmail', { ns: 'auth' }),
    securityNotice: t('confirmEmail.securityNoticeHaveConfirmed', { ns: 'auth' }),
    emailInstruction: t('confirmEmail.emailInstruction', { ns: 'auth' }),
    emailHighlight: newEmail,
    closeButton: t('confirmEmail.close', { ns: 'auth' }),
    closeButtonHint: t('confirmEmail.confirmReceived', { ns: 'auth' }),
    resendButton: t('confirmEmail.resend', { ns: 'auth' }),
    editButton: t('changeEmail', { ns: 'auth' }),
  };

  const text3 = {
    title: t('changeEmail', { ns: 'auth' }),
    heading: t('confirmEmail.newEmailIsNowEffective', { ns: 'auth' }),
    securityNotice: t('confirmEmail.securityNoticeCanContinue', { ns: 'auth' }),
    closeButton: t('confirmEmail.close', { ns: 'auth' }),
  };

  const showEmailAndPassword = !isOnboarding;

  return (
    <>
      <div className="flex flex-col md:flex-row w-full">
        {/* Password Form - hidden during onboarding */}
        {showEmailAndPassword && (
          <form onSubmit={handleUpdatePassword} className="flex-1 md:basis-1/3 bg-white p-6 pt-0">
            <label className="block text-secondary font-ambit text-base font-bold mb-4">
              {t('improveProfileStep.security.changePassword', { ns: 'auth' })}
            </label>

            {/* Current Password */}
            <div className="mb-4 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-light">
                <Lock size={20} />
              </span>
              <input
                type={showCurrent ? 'text' : 'password'}
                className="w-full bg-[#E1E9F0] rounded px-10 py-3 font-ambit text-xs text-secondary border-none focus:outline-none pr-12"
                placeholder={t('improveProfileStep.security.currentPassword', { ns: 'auth' })}
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                onClick={() => setShowCurrent(v => !v)}
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* New Password */}
            <div className="mb-4 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-light">
                <Lock size={20} />
              </span>
              <input
                type={showNew ? 'text' : 'password'}
                className={`w-full pl-10 py-3 rounded-md font-ambit text-secondary text-xs border-none focus:outline-none pr-12 ${
                  passwordErrors.length > 0
                    ? 'border border-[#F04438] bg-[#FEE4E2]'
                    : 'bg-[#E1E9F0]'
                }`}
                placeholder={t('improveProfileStep.security.newPassword', { ns: 'auth' })}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                onClick={() => setShowNew(v => !v)}
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {passwordErrors.length > 0 && (
              <ul className="mt-2 text-sm text-red-600 space-y-2">
                {passwordErrors.map((err, idx) => (
                  <li key={idx} className="flex items-start">
                    {t(`errors.${err}`, { ns: 'auth' })}
                  </li>
                ))}
              </ul>
            )}

            {passwordError && <p className="font-ambit mt-2 text-[#F04438]">{passwordError}</p>}

            <div className="mt-6">
              <button
                type="submit"
                className={`w-full font-ambit font-bold rounded-full px-5 py-3 text-xs transition-colors cursor-pointer ${isPasswordFormValid ? 'bg-primary text-white' : 'bg-[#E1E9F0] text-secondary-light'}`}
                disabled={!isPasswordFormValid}
              >
                {t('improveProfileStep.security.changePassword', { ns: 'auth' })}
              </button>
            </div>

            <ul className="list-disc list-outside my-4 pl-6 text-secondary space-y-2 text-sm">
              <li>{t('errors.passwordTooShort', { ns: 'auth' })}</li>
              <li>{t('errors.passwordNumberOrSpecial', { ns: 'auth' })}</li>
              <li>{t('errors.passwordUpperLower', { ns: 'auth' })}</li>
            </ul>
          </form>
        )}

        {/* Email Form - hidden during onboarding */}
        {showEmailAndPassword && (
          <form onSubmit={handleUpdateEmail} className="flex-1 md:basis-1/3 bg-white p-6 pt-0">
            <label className="block text-secondary font-ambit text-base font-bold mb-4">
              {t('changeEmail', { ns: 'auth' })}
            </label>

            <div className="mb-4 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-light">
                <Mail size={20} />
              </span>
              <input
                type="email"
                className="w-full bg-[#E1E9F0] rounded px-10 py-3 font-ambit text-xs text-secondary border-none focus:outline-none"
                placeholder={t('confirmEmail.oldEmail', { ns: 'auth' })}
                value={oldEmail}
                onChange={e => setOldEmail(e.target.value)}
              />
            </div>

            <div className="mb-4 relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-secondary-light">
                <Mail size={20} />
              </span>
              <input
                type="email"
                className={`
                       w-full bg-[#E1E9F0] rounded px-10 py-3 font-ambit text-xs text-primary
                        ${
                          emailError
                            ? 'border border-[#F04438] bg-[#FEE4E2]'
                            : 'border border-gray-300 bg-[#E1E9F0] focus:ring-blue-500 focus:border-blue-500'
                        }
                      `}
                placeholder={t('confirmEmail.newEmail', { ns: 'auth' })}
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
              />
            </div>
            {emailError && <p className="font-ambit mt-2 text-[#F04438]">{emailError}</p>}

            <div className="mt-6">
              <button
                type="submit"
                className={`w-full font-ambit font-bold rounded-full px-5 py-3 text-xs transition-colors ${isEmailFormValid ? 'bg-primary text-white' : 'bg-[#E1E9F0] text-secondary-light'}`}
                disabled={!isEmailFormValid}
              >
                {t('changeEmail', { ns: 'auth' })}
              </button>
            </div>
          </form>
        )}

        {/* 2FA Section - always shown */}
        <div
          className={`flex-1 bg-white flex flex-col justify-start gap-4 ${showEmailAndPassword ? 'md:basis-1/3 p-6 pt-0' : ''}`}
        >
          <EnrollMFA />
        </div>
      </div>

      {/* Next/Save/Skip/Back buttons for onboarding */}
      {(onNext || onSave || onSkip || onPrevious) && (
        <div className="flex flex-col gap-4 md:flex-row-reverse mt-8">
          <Button
            variant="primary"
            onClick={() => {
              if (onNext) {
                onNext();
              } else if (onSave) {
                onSave();
              }
            }}
            className="px-10 py-1"
          >
            {onNext
              ? t('common:form_actions.next', { ns: 'common' })
              : t('improveProfileStep.save', { ns: 'auth' })}
          </Button>

          {onSkip && (
            <Button variant="transparent-with-border" onClick={onSkip} className="px-10 py-1">
              {t('common:form_actions.skip', { ns: 'common' })}
            </Button>
          )}

          {onPrevious && (
            <Button
              variant="simple-text"
              onClick={onPrevious}
              className="px-10 py-1 flex items-center gap-2"
            >
              <ChevronLeft size={20} aria-hidden />
              {t('common:form_actions.previous', { ns: 'common' })}
            </Button>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={isCurrentEmailConfirmationOpen}
        currentEmail={t('confirmEmail.currentAddress', { ns: 'auth' })}
        text={text}
        onClose={() => {
          setIsCurrentEmailConfirmationOpen(false);
          setIsNewEmailConfirmationOpen(true);
        }}
        onResend={() => resendConfirmationEmail(newEmail)}
        onEdit={() => {
          setIsNewEmailConfirmationOpen(false);
          setIsEmailChangedOpen(false);
          setIsCurrentEmailConfirmationOpen(false);
        }}
      />

      <ConfirmationModal
        isOpen={isNewEmailConfirmationOpen}
        newEmail={t('confirmEmail.newAddress', { ns: 'auth' })}
        text={text2}
        onClose={() => {
          setIsNewEmailConfirmationOpen(false);
          setIsEmailChangedOpen(true);
        }}
        onResend={() => {
          resendConfirmationEmail(newEmail);
          setIsNewEmailConfirmationOpen(false);
          setIsCurrentEmailConfirmationOpen(true);
        }}
        onEdit={() => {
          setIsNewEmailConfirmationOpen(false);
          setIsEmailChangedOpen(false);
          setIsCurrentEmailConfirmationOpen(false);
        }}
      />

      <ConfirmationModal
        isOpen={isEmailChangedOpen}
        text={text3}
        onClose={() => {
          setIsEmailChangedOpen(false);
          setNewEmail('');
          setOldEmail('');
        }}
      />
    </>
  );
};

export default EditProfileSecurity;
